import React, { useState, useEffect } from 'react';
import { useLatestVersion } from '../data/changelogData';
import './Legal.css';

function PrivacyPolicy() {
  const version = useLatestVersion();
  const [activeTab, setActiveTab] = useState('ingestion');
  
  const sections = [
    { id: 'ingestion', title: 'Technical Ingestion', icon: 'fa-database' },
    { id: 'intelligence', title: 'Platform Intelligence', icon: 'fa-brain' },
    { id: 'persistence', title: 'Security & Persistence', icon: 'fa-server' },
    { id: 'api-proxy', title: 'API Proxy Protocols', icon: 'fa-network-wired' },
    { id: 'governance', title: 'Universal Governance', icon: 'fa-gavel' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + 130;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element && element.offsetTop <= scrollPos && (element.offsetTop + element.offsetHeight) > scrollPos) {
          setActiveTab(section.id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  return (
    <div className="legal-gov-container animate-up">
      <aside className="legal-gov-sidebar">
        <ul className="legal-gov-toc">
          {sections.map(sec => (
            <li key={sec.id} className="legal-gov-toc-item">
              <a 
                href={`#${sec.id}`} 
                className={`legal-gov-toc-link ${activeTab === sec.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(sec.id).scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <i className={`fa-solid ${sec.icon}`} style={{width: '20px', marginRight: '10px', fontSize: '0.8rem'}}></i>
                {sec.title}
              </a>
            </li>
          ))}
        </ul>
      </aside>

      <main className="legal-gov-content">
        <div className="legal-gov-seal">
          <i className="fa-solid fa-shield-halved"></i>
        </div>

        <article className="legal-gov-document">
          <header className="legal-gov-header">
            <h1 className="legal-gov-title">Privacy Protocols</h1>
            <div className="legal-gov-meta">GOVERNANCE_PROTOCOL: {version} | EXECUTED_MARCH_2026</div>
          </header>

          <section id="ingestion" className="legal-gov-section">
            <h2>1. Technical Ingestion</h2>
            <p>
              DevTracker operates on a principle of absolute technical minimalism. During the instantiation 
              of your neutral dossier, the platform ingests only the metadata necessary to construct high-fidelity 
              professional visualizations. This includes:
            </p>
            <ul>
              <li><strong>Authentication Hashes:</strong> Cryptographically secure identifiers linked to your SUID.</li>
              <li><strong>Platform Handles:</strong> Public-facing usernames for GitHub, LeetCode, and Codeforces manually provided by the operator.</li>
              <li><strong>Telemetry Stream:</strong> Anonymous performance metrics for internal optimization.</li>
            </ul>
            <p>
              The platform strictly prohibits the request, storage, or transit of third-party credentials or 
              private system keys. All ingestion occurs over TLS 1.3 secured channels.
            </p>
          </section>

          <section id="intelligence" className="legal-gov-section">
            <h2>2. Platform Intelligence</h2>
            <p>
              The <strong>DAIV-2000</strong> logic engine identifies professional trajectory patterns 
              by analyzing public technical contributions. This analysis generates your <strong>DNA Score</strong> 
              and AI-driven persona. 
            </p>
            <p>
              This intelligence is functionally proprietary to your account. DevTracker does not facilitate the 
              harvesting or distribution of your neural dossier to external data brokers, recruitment agencies, 
              or third-party AI trainers without an explicit authority signature linked to your account ID.
            </p>
          </section>

          <section id="persistence" className="legal-gov-section">
            <h2>3. Security & Persistence</h2>
            <p>
              Platform architecture is redundant and secured via standard industry encryption. Data at rest 
              is persistence-mapped via Firebase AES-256 equivalent infrastructure.
            </p>
            <p>
              Authorization is enforced at the database level through strict SUID-governed security rules. 
              The <strong>Neural Authority</strong> framework ensures that cross-profile introspection is 
              operationally impossible without specific administrative clearance tokens.
            </p>
          </section>

          <section id="api-proxy" className="legal-gov-section">
            <h2>4. API Proxy Protocols</h2>
            <p>
              To ensure millisecond response fidelity, DevTracker utilizes hardened API proxies to 
              interface with synchronized platforms. These proxies operate in a strictly read-only 
              capacity within the defined public API scopes of the target services.
            </p>
            <p>
              Continued use of the platform constitutes an operational acknowledgement of these 
              ingestion protocols as the factual baseline for your professional representation.
            </p>
          </section>

          <section id="governance" className="legal-gov-section">
            <h2>5. Universal Governance</h2>
            <p>
              This document is subject to the overarching <strong>Professional Governance Standard</strong> 
              of the DevTracker ecosystem. Significant shifts in cryptographic handling or data 
              persistence geography will be broadcasted via the Platform Intelligence Hub.
            </p>
            <p>
              Operators retain the absolute right to <strong>Purge Dossier</strong>—an atomic action 
              accessible via the Platform Control Center that results in the immediate nullification 
              of all associated persistent nodes.
            </p>
          </section>

          <div style={{marginTop: '60px', paddingTop: '30px', borderTop: '1px solid var(--border-color)', opacity: 0.5, fontSize: '0.75rem', fontFamily: 'monospace'}}>
            AUTHORITY_HASH: 0X-DT-PRIV-PROTOCOL-V4_0_0_9-SYNC-ACTIVE
          </div>
        </article>
      </main>
    </div>
  );
}

export default PrivacyPolicy;
